import React from "react";
import { Link } from "react-router-dom";
import { displayProgramTitle, TRAINING } from "./trainingTerms";
import {
  TrainingAcademyTile,
  TrainingHero,
  TrainingLoading,
  TrainingProgressBar,
  TrainingSignInBanner,
  countAcademyModules,
  findNextModule,
} from "./AcademyShared";

export default function AcademyHome({ catalog, home, baseUrl, isLoggedIn, loading }) {
  const academies = catalog?.academies || [];
  const homeAcademies = home?.academies || [];
  const overall = home?.overall_pct ?? 0;
  const modulesDone = home?.modules_done ?? 0;
  const modulesTotal = home?.modules_total ?? 0;
  const mandatoryPending = home?.mandatory_pending_count ?? 0;
  const next = findNextModule(homeAcademies);

  const progressBySlug = {};
  for (const academy of homeAcademies) {
    const { total, done } = countAcademyModules([academy]);
    progressBySlug[academy.slug] = total ? Math.round((done / total) * 100) : 0;
  }

  if (loading) {
    return <TrainingLoading label={`Loading ${TRAINING.programs.toLowerCase()}...`} />;
  }

  return (
    <>
      <TrainingHero
        eyebrow="QikTruck Careers"
        title="Learn logistics. Build your career."
        subtitle={`Five career ${TRAINING.programs.toLowerCase()} with ${TRAINING.modules.toLowerCase()}, quizzes, and certificates. Start free on any device.`}
      />

      {isLoggedIn && home && (
        <>
          <div className="training-stats">
            <div className="training-stat">
              <span className="training-stat-value">{modulesDone}</span>
              <span className="training-stat-label">Modules done</span>
            </div>
            <div className="training-stat">
              <span className="training-stat-value">{modulesTotal - modulesDone}</span>
              <span className="training-stat-label">Remaining</span>
            </div>
            <div className="training-stat">
              <span className="training-stat-value">{mandatoryPending}</span>
              <span className="training-stat-label">Required left</span>
            </div>
          </div>

          <TrainingProgressBar value={overall} label="Your overall progress" />

          {home.continue_module && (
            <Link
              to={`${baseUrl}/${home.continue_module.academy_slug}/module/${home.continue_module.slug}`}
              className="training-continue-card"
            >
              <div className="training-continue-label">Continue learning</div>
              <div className="training-continue-title">{home.continue_module.title}</div>
              <div className="training-continue-meta">
                {home.continue_module.academy_slug?.replace(/-/g, " ")}
                {home.continue_module.watch_pct > 0 ? ` · ${home.continue_module.watch_pct}% done` : ""}
              </div>
            </Link>
          )}

          {next && !home.continue_module && (
            <Link
              to={`${baseUrl}/${next.academy.slug}/module/${next.mod.slug}`}
              className="training-continue-card"
            >
              <div className="training-continue-label">Up next</div>
              <div className="training-continue-title">{next.mod.title}</div>
              <div className="training-continue-meta">{displayProgramTitle(next.academy.title)}</div>
            </Link>
          )}
        </>
      )}

      {!isLoggedIn && <TrainingSignInBanner nextPath="/academy" />}

      <div className="training-how">
        <h3 className="training-how-title">How it works</h3>
        <ol className="training-how-list">
          <li>
            <span className="training-how-num">1</span>
            <span>Pick a {TRAINING.program.toLowerCase()} and open a {TRAINING.module.toLowerCase()}. Complete each {TRAINING.lesson.toLowerCase()} and pass the {TRAINING.quiz.toLowerCase()} at the end.</span>
          </li>
          <li>
            <span className="training-how-num">2</span>
            <span>Track your progress and build a profile recruiters can see.</span>
          </li>
          <li>
            <span className="training-how-num">3</span>
            <span>Move through the QikTruck careers pipeline toward placement.</span>
          </li>
        </ol>
      </div>

      <h3 className="training-section-title">All {TRAINING.programs.toLowerCase()}</h3>

      {academies.length === 0 ? (
        <div className="training-empty">No {TRAINING.programs.toLowerCase()} available yet. Check back soon.</div>
      ) : (
        <div className="training-academy-grid">
          {academies.map((a) => (
            <TrainingAcademyTile
              key={a.id}
              academy={a}
              to={`${baseUrl}/${a.slug}`}
              progressPct={progressBySlug[a.slug]}
            />
          ))}
        </div>
      )}
    </>
  );
}
